export interface CreatorScheduleConflict {
  status: 'yes' | 'no';
  unavailableStartDate: Date | null;
  unavailableEndDate: Date | null;
  notes: string;
}

export const createDefaultCreatorScheduleConflict =
  (): CreatorScheduleConflict => ({
    status: 'no',
    unavailableStartDate: null,
    unavailableEndDate: null,
    notes: ''
  });

const startOfDay = (date: Date) => {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  return result;
};

const endOfDay = (date: Date) => {
  const result = new Date(date);
  result.setHours(23, 59, 59, 999);
  return result;
};

export const parseScheduleConflictDate = (
  value?: string | Date | null
): Date | null => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (value.includes('T')) {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  const day = Number(match[3]);
  const date = new Date(year, month, day);
  return date.getFullYear() === year &&
    date.getMonth() === month &&
    date.getDate() === day
    ? date
    : null;
};

export const buildCreatorScheduleConflictPayload = (
  schedule: CreatorScheduleConflict
) => ({
  status: schedule.status,
  ...(schedule.status === 'yes' &&
  schedule.unavailableStartDate &&
  schedule.unavailableEndDate
    ? {
        // DatePicker 只选择日期；提交时扩展为用户当地时区完整的自然日范围，
        // 再序列化为 UTC ISO 字符串。
        unavailableStartDate: startOfDay(
          schedule.unavailableStartDate
        ).toISOString(),
        unavailableEndDate: endOfDay(schedule.unavailableEndDate).toISOString(),
        ...(schedule.notes.trim() ? { notes: schedule.notes.trim() } : {})
      }
    : {})
});

export const isCreatorScheduleConflictValid = (
  schedule: CreatorScheduleConflict,
  now = new Date()
) => {
  if (schedule.status === 'no') return true;
  if (!schedule.unavailableStartDate || !schedule.unavailableEndDate) {
    return false;
  }
  const startDate = startOfDay(schedule.unavailableStartDate);
  const endDate = startOfDay(schedule.unavailableEndDate);
  return startDate >= startOfDay(now) && endDate >= startDate;
};

/** No 也属于已提交；空对象、null 和 undefined 均不属于已提交。 */
export const hasSubmittedScheduleConflict = (scheduleConflict: unknown) => {
  if (
    scheduleConflict == null ||
    typeof scheduleConflict !== 'object' ||
    Array.isArray(scheduleConflict)
  ) {
    return false;
  }
  const status = (scheduleConflict as { status?: unknown }).status;
  return status === 'yes' || status === 'no';
};

export const formatScheduleConflictDateRange = (
  startValue: string | Date | null,
  endValue: string | Date | null,
  locale = 'en-US'
) => {
  const startDate = parseScheduleConflictDate(startValue);
  const endDate = parseScheduleConflictDate(endValue);
  if (!startDate || !endDate) return '';

  const resolvedLocale = locale || 'en-US';
  const startDay = startDate.getDate();
  const endDay = endDate.getDate();
  const startYear = startDate.getFullYear();
  const endYear = endDate.getFullYear();
  const sameMonth =
    startYear === endYear && startDate.getMonth() === endDate.getMonth();

  if (resolvedLocale.startsWith('zh')) {
    if (sameMonth) {
      return startDay === endDay
        ? `${startYear}年${startDate.getMonth() + 1}月${startDay}日`
        : `${startYear}年${startDate.getMonth() + 1}月${startDay}日–${endDay}日`;
    }
    return `${startYear}年${startDate.getMonth() + 1}月${startDay}日–${endYear}年${endDate.getMonth() + 1}月${endDay}日`;
  }
  if (resolvedLocale.startsWith('ja')) {
    if (sameMonth) {
      return startDay === endDay
        ? `${startYear}年${startDate.getMonth() + 1}月${startDay}日`
        : `${startYear}年${startDate.getMonth() + 1}月${startDay}日〜${endDay}日`;
    }
    return `${startYear}年${startDate.getMonth() + 1}月${startDay}日〜${endYear}年${endDate.getMonth() + 1}月${endDay}日`;
  }

  const monthFormatter = new Intl.DateTimeFormat(resolvedLocale, {
    month: 'long'
  });
  const startMonth = monthFormatter.format(startDate);
  const endMonth = monthFormatter.format(endDate);
  if (sameMonth) {
    return startDay === endDay
      ? `${startMonth} ${startDay}, ${startYear}`
      : `${startMonth} ${startDay}–${endDay}, ${startYear}`;
  }
  if (startYear === endYear) {
    return `${startMonth} ${startDay}–${endMonth} ${endDay}, ${startYear}`;
  }
  return `${startMonth} ${startDay}, ${startYear}–${endMonth} ${endDay}, ${endYear}`;
};
